import { t } from '../../services/locale';
import type {
  ProfileAccountBranch,
  ProfileAccountFormatters,
  ProfileTransaction,
  ProfileTransactionStatusTag,
} from './profileAccount.types';

const DEFAULT_TIME_FORMAT = 'HH:mm';
const PENDING_STATUSES = ['PENDING', 'PENDING_INTENT'];

/**
 * Same word splitting as lodash `upperCase`, which the controller uses:
 * `credit_card` → `CREDIT CARD`, `stripeCard` → `STRIPE CARD`, `3dSecure` → `3 D SECURE`.
 */
export function upperCaseWords(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
}

/** `DD MMMM YYYY, <branch time format>` as used by `getTransactionDate`. */
export function getTransactionDateFormat(branch: ProfileAccountBranch | null | undefined): string {
  const timeFormat = branch?.configuration?.formats?.time || DEFAULT_TIME_FORMAT;
  return `DD MMMM YYYY, ${timeFormat}`;
}

/** Port of `the profile dialog getTransactionDate`; `created` is unix seconds rendered in the branch timezone. */
export function getTransactionDate(
  transaction: Pick<ProfileTransaction, 'created'>,
  branch: ProfileAccountBranch | null | undefined,
  formatUnix: ProfileAccountFormatters['formatUnix']
): string {
  return formatUnix(transaction.created, getTransactionDateFormat(branch));
}

function hasKeys(value: object | null | undefined): value is object {
  return !!value && Object.keys(value).length > 0;
}

function getModelName(model: NonNullable<ProfileTransaction['model_obj']>): string {
  if (model.first_name && model.last_name) {
    return `${model.first_name} ${model.last_name}`;
  }
  return model.name || '';
}

function getMetadataName(metadata: NonNullable<ProfileTransaction['metadata']>): string {
  if (metadata.description) {
    return metadata.description;
  }
  return metadata.glofox_event === 'custom_charge' ? t('WEB_CUSTOM_CHARGE') : '';
}

/** Port of `the profile dialog getTransactionName`: model wins over metadata; person name wins over model name. */
export function getTransactionName(transaction: ProfileTransaction): string {
  const model = transaction.model_obj;
  const metadata = transaction.metadata;

  if (hasKeys(model)) {
    return getModelName(model);
  }
  if (hasKeys(metadata)) {
    return getMetadataName(metadata);
  }
  return '';
}

/** Port of `the profile dialog getTransactionPaymentMethod`. */
export function getTransactionPaymentMethod(transaction: ProfileTransaction): string {
  return upperCaseWords(transaction.metadata?.payment_method);
}

/** Port of `the profile dialog getTransactionType`. */
export function getTransactionType(transaction: ProfileTransaction): string {
  const event = transaction.metadata?.glofox_event;
  if (event === 'NSF_FEE') {
    return t('WEB_NSF_FEE').toUpperCase();
  }
  if (event) {
    return upperCaseWords(event);
  }
  return t('WEB_STRIPE_CHARGE');
}

/** Which status chip the row shows (`profile-transaction-<i>-status-pending` / `-status-failed`). */
export function getTransactionStatusTag(transaction: Pick<ProfileTransaction, 'transaction_status'>): ProfileTransactionStatusTag {
  const status = transaction.transaction_status;
  if (status && PENDING_STATUSES.includes(status)) {
    return 'pending';
  }
  if (status === 'ERROR') {
    return 'failed';
  }
  return null;
}

/** Class of the type chip (`profile-transaction-<i>-type`). */
export function getTransactionTypeTagClass(transaction: Pick<ProfileTransaction, 'paid'>): 'blue-tag' | 'red-tag' {
  return transaction.paid ? 'blue-tag' : 'red-tag';
}
